import { useTranslate, type Identifier } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { PipelineStage } from "../../types";

export interface DeleteStageDialogProps {
  stage: PipelineStage | null;
  /** Number of cards in the stage, including the ones hidden by filters. */
  cardCount: number;
  otherStages: PipelineStage[];
  onClose: () => void;
  /** Moves the cards to `targetStageId` (when there are cards), then deletes. */
  onConfirm: (targetStageId: Identifier | null) => Promise<void>;
}

export const DeleteStageDialog = ({
  stage,
  cardCount,
  otherStages,
  onClose,
  onConfirm,
}: DeleteStageDialogProps) => {
  const translate = useTranslate();
  const [targetId, setTargetId] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setTargetId(otherStages[0] ? String(otherStages[0].id) : "");
  }, [stage, otherStages]);

  if (!stage) return null;
  const isLastStage = otherStages.length === 0;
  const target = otherStages.find(
    (candidate) => String(candidate.id) === targetId,
  );

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm(cardCount > 0 ? (target?.id ?? null) : null);
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate("crm.operations.stages.delete_title", {
              name: stage.name,
            })}
          </DialogTitle>
          <DialogDescription>
            {isLastStage
              ? translate("crm.operations.stages.last_stage")
              : cardCount > 0
                ? translate("crm.operations.stages.delete_with_cards", {
                    smart_count: cardCount,
                  })
                : translate("crm.operations.stages.delete_empty")}
          </DialogDescription>
        </DialogHeader>
        {!isLastStage && cardCount > 0 && (
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger
              className="w-full"
              aria-label={translate("crm.operations.stages.move_cards_to")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {otherStages.map((candidate) => (
                <SelectItem key={candidate.id} value={String(candidate.id)}>
                  {candidate.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isLastStage || isDeleting || (cardCount > 0 && !target)}
            onClick={handleConfirm}
          >
            {translate("crm.operations.stages.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
